import React from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, Quote, Building2, Shield, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Sarah Johnson',
      title: 'IT Director',
      company: 'TechForward Manufacturing',
      industry: 'Manufacturing',
      rating: 5,
      text: "Zentech Solutions transformed our entire IT infrastructure. Their managed services approach has given us 99.9% uptime and significantly reduced our security risks. The team is incredibly responsive and proactive in preventing issues before they impact our operations.",
      services: ['Managed IT', 'Cybersecurity', '24/7 Monitoring']
    },
    {
      name: 'Dr. Michael Chen',
      title: 'Chief Information Officer',
      company: 'HealthCare Plus',
      industry: 'Healthcare',
      rating: 5,
      text: "Working with Zentech has been a game-changer for our healthcare organization. Their expertise in GDPR compliance and data protection has kept our patient information secure while improving our operational efficiency. I highly recommend their cybersecurity services.",
      services: ['GDPR Compliance', 'Data Protection', 'Healthcare IT']
    },
    {
      name: 'James Rodriguez',
      title: 'Operations Manager',
      company: 'FinanceFlow Solutions',
      industry: 'Financial Services',
      rating: 5,
      text: "The level of expertise and professionalism from Zentech Solutions is outstanding. Their managed IT services have optimized our trading systems, reduced latency by 95%, and provided the security we need in the financial sector. Exceptional service delivery.",
      services: ['Network Optimization', 'Financial IT', 'Security Monitoring']
    },
    {
      name: 'Emma Thompson',
      title: 'Managing Director',
      company: 'Creative Design Studio',
      industry: 'Creative Services',
      rating: 5,
      text: "As a creative agency, we need our technology to work seamlessly. Zentech's managed IT services have eliminated our tech headaches, allowing us to focus on what we do best. Their cloud solutions have revolutionized how we collaborate with clients.",
      services: ['Cloud Services', 'Creative IT', 'Remote Support']
    },
    {
      name: 'Robert Wilson',
      title: 'CEO',
      company: 'GreenTech Innovations',
      industry: 'Clean Technology',
      rating: 5,
      text: "Zentech Solutions doesn't just provide IT support - they're true partners in our business growth. Their proactive approach to cybersecurity and system management has saved us countless hours and protected our intellectual property. Outstanding service!",
      services: ['Business IT', 'IP Protection', 'Growth Support']
    },
    {
      name: 'Lisa Parker',
      title: 'Finance Director',
      company: 'Parker & Associates',
      industry: 'Professional Services',
      rating: 5,
      text: "The cost savings and efficiency improvements since partnering with Zentech have been remarkable. Their managed services model is perfect for our growing firm, and their cybersecurity expertise gives us confidence in our data protection capabilities.",
      services: ['Cost Optimization', 'Professional Services IT', 'Scalable Solutions']
    }
  ];

  const stats = [
    { value: '98%', label: 'Client Retention Rate' },
    { value: '24/7', label: 'Support Availability' },
    { value: '4.9/5', label: 'Average Rating' },
    { value: '500+', label: 'Happy Clients' }
  ];

  const industryLogos = [
    'Manufacturing', 'Healthcare', 'Financial Services', 'Technology', 
    'Professional Services', 'Education', 'Retail', 'Non-Profit'
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Client Testimonials
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 leading-relaxed">
              Hear from our satisfied clients about how our managed IT services and 
              cybersecurity solutions have transformed their businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center border-none shadow-lg">
                <CardContent className="p-8">
                  <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Real testimonials from real clients who have experienced the benefits of our 
              managed IT services and cybersecurity expertise.
            </p>
          </div>

          <Carousel 
            className="w-full" 
            opts={{ align: "start", loop: true }}
            plugins={[
              Autoplay({
                delay: 2000,
              }),
            ]}
          >
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                  <Card className="border-none shadow-lg hover:shadow-xl transition-shadow h-full">
                    <CardContent className="p-8">
                      {/* Rating */}
                      <div className="flex items-center space-x-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-current text-yellow-400" />
                        ))}
                      </div>

                      {/* Quote */}
                      <div className="relative mb-6">
                        <Quote className="h-8 w-8 text-primary/20 absolute -top-2 -left-2" />
                        <p className="text-muted-foreground italic pl-6 text-sm">{testimonial.text}</p>
                      </div>

                      {/* Client Info */}
                      <div className="border-t border-border pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-foreground text-sm">{testimonial.name}</h4>
                            <p className="text-xs text-muted-foreground">{testimonial.title}</p>
                            <p className="text-xs text-primary font-medium">{testimonial.company}</p>
                            <p className="text-xs text-muted-foreground mt-1">{testimonial.industry}</p>
                          </div>
                          <Building2 className="h-6 w-6 text-primary/30" />
                        </div>

                        {/* Services */}
                        <div className="flex flex-wrap gap-1 mt-3">
                          {testimonial.services.slice(0, 3).map((service, serviceIndex) => (
                            <Badge key={serviceIndex} variant="secondary" className="text-xs px-2 py-1">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our managed IT services and cybersecurity solutions are trusted by businesses 
              across diverse industries throughout the UK.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {industryLogos.map((industry, index) => (
              <Card key={index} className="text-center border-none shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="text-sm text-foreground font-medium">{industry}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="text-center border-none shadow-lg">
              <CardContent className="p-8">
                <Shield className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Certified Professionals</h3>
                <p className="text-muted-foreground">
                  Our team holds industry-leading certifications in cybersecurity and IT management.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-none shadow-lg">
              <CardContent className="p-8">
                <Users className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Dedicated Support</h3>
                <p className="text-muted-foreground">
                  24/7 expert support ensures your business technology runs smoothly at all times.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-none shadow-lg">
              <CardContent className="p-8">
                <Building2 className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-3">Business Focused</h3>
                <p className="text-muted-foreground">
                  We understand your business needs and tailor our IT solutions accordingly.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground border-none">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Join Our Satisfied Clients
              </h2>
              <p className="text-xl text-primary-foreground/90 mb-8 max-w-3xl mx-auto">
                Experience the same level of service and expertise that has earned us these 
                testimonials. Contact us today for your free IT consultation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild variant="secondary" size="lg">
                  <Link to="/contact">Get Your Free Consultation</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  <Link to="/case-studies">View Case Studies</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default Testimonials;